import React, { useEffect, useState, useCallback, useMemo } from "react";
import { auth } from "../firebase";
import { useNotifications } from "../hooks/notificationsContext";

export default function CompanyShareholders({ ticker, onSelectedPlayer }) {
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { addMessage } = useNotifications();

  const isCompany = useMemo(() => {
    if (!ticker) return false;

    const isTickerPattern = /^[A-Z]{1,5}$/.test(ticker);
    const isBond = ["BND3", "BND7", "BND14"].includes(ticker);

    return isTickerPattern || isBond;
  }, [ticker]);

  useEffect(() => {
    if (!ticker) return;

    if (!isCompany) {
      setShareholders([]);
      setError("");
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchShareholders = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(
          "https://us-central1-stockmarket-83a73.cloudfunctions.net/getCompanyShareholders",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ticker }),
          }
        );

        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!isCancelled) setShareholders(data);
      } catch (err) {
        if (!isCancelled) {
          setError(err.message);
          addMessage("Error fetching shareholders.", "error");
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchShareholders();
    return () => { isCancelled = true; };
  }, [ticker, isCompany, addMessage]);

  const handleNameClick = useCallback((playerName) => {
    if (typeof onSelectedPlayer === "function") {
      onSelectedPlayer(playerName);
    }
  }, [onSelectedPlayer]);

  if (!isCompany) {
    return <div className="tab-content" />;
  }

  if (loading) return (
    <div className="tab-content">
      <p className="terminal-status">LOADING...</p>
    </div>
  );
  
  if (error) return (
    <div className="tab-content">
      <p className="terminal-status error">ERROR: {error}</p>
    </div>
  );
  
  if (!shareholders.length) return (
    <div className="tab-content">
      <p className="terminal-status">NO SHAREHOLDERS FOUND.</p>
    </div>
  );

  return (
    <div className="tab-content" style={{ padding: 0 }}>
      <div className="market-table-container">
        <table className="market-table">
          <thead>
            <tr>
              <th className="text-left">Shareholder</th>
              <th className="text-right">Holdings</th>
              <th className="text-right">Value ($)</th>
              <th className="text-right">Stake %</th>
            </tr>
          </thead>
          <tbody>
            {shareholders.map((holder, idx) => (
              <tr key={idx} className="table-row">
                <td 
                  className="text-left clickable-shareholder" 
                  onClick={() => handleNameClick(holder.name)}
                >
                  {holder.name.toUpperCase()}
                </td>
                <td className="text-right tabular-nums">{holder.holdings.toLocaleString()}</td>
                <td className="text-right tabular-nums">${holder.value.toLocaleString()}</td>
                <td className="text-right font-mono teal-highlight">{holder.share}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}